import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import {
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ContentWrapper from "../Layout/ContentWrapper";
import { getLeaderboardAction } from "../../store/reducers/results";

// ── All known contests, newest first ─────────────────────────────────────────
// Sourced from mainapp-prd public.contests as of 2026-06-25.
// Add new rows here as contests are created.
const CONTESTS = [
  { id: 44, name: "World Cup Group Stage",                     type: "worldcup_group" },
  { id: 43, name: "2026 Men's Sweet 16 and Elite 8",           type: "qna" },
  { id: 42, name: "2026 Men's Bracket Challenge",              type: "bracket" },
  { id: 41, name: "2026 Women's Bracket Challenge",            type: "qna" },
  { id: 40, name: "2026 Men's Bracket Challenge (Feb)",        type: "bracket" },
  { id: 39, name: "Super Bowl Challenge",                      type: "qna" },
  { id: 38, name: "Spotlight Grammy Predictions",              type: "qna" },
  { id: 37, name: "2026 College Football Championship",        type: "qna" },
  { id: 36, name: "2026 NFL Wild Card Weekend",                type: "qna" },
  { id: 35, name: "Men's 2026 Bracket Challenge",              type: "qna" },
  { id: 34, name: "Spotlight Super Bowl Run",                  type: "qna" },
  { id: 33, name: "Spotlight College Football Playoff Showdown", type: "qna" },
  { id: 32, name: "Spotlight New Year's Showdown",             type: "qna" },
  { id: 31, name: "Spotlight Christmas Day Showdown",          type: "qna" },
  { id: 30, name: "Spotlight Thanksgiving Day Football Showdown", type: "qna" },
  { id: 29, name: "Spotlight College Men's Showdown (Nov 10–16)", type: "qna" },
  { id: 28, name: "NBA Weekly Contest (Nov 3–8)",              type: "qna" },
  { id: 27, name: "NFL Week 9 Pick'em",                        type: "qna" },
  { id: 26, name: "NFL Week 8 Pick'em",                        type: "qna" },
  { id: 25, name: "NBA Weekly Contest (Oct 23–30)",            type: "qna" },
];

// ── Helpers (mirrors Leaderboard/index.js) ────────────────────────────────────
const displayHandle = (row) => {
  if (row?.unique_name) return row.unique_name;
  const id = row?.spotlight_user_id;
  if (!id) return "Player";
  return `Player ${String(id).slice(0, 4).toUpperCase()}`;
};

const initials = (name) => {
  const cleaned = String(name || "").replace(/[^a-zA-Z0-9]/g, "");
  return cleaned.slice(0, 2).toUpperCase() || "?";
};

const AVATAR_COLORS = [
  "#2F6BFF", "#7C3AED", "#10B981", "#F59E0B",
  "#EC4899", "#06B6D4", "#6366F1", "#EF4444",
];
const avatarColor = (key) => {
  const s = String(key || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

const hasTiebreaker = (score) => score != null && score !== -1;

// ── Component ─────────────────────────────────────────────────────────────────
const LeaderboardExplorer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  const userId    = useSelector((state) => state.app?.userId);
  const leaderboard = useSelector((state) => state.results?.leaderboard);
  const meta      = useSelector((state) => state.results?.leaderboardMeta);
  const loading   = useSelector((state) => state.results?.leaderboardLoading);

  const [selectedId, setSelectedId] = useState(CONTESTS[0].id);

  useEffect(() => {
    if (!selectedId) return;
    dispatch(getLeaderboardAction({
      userId,
      contestId: selectedId,
      limit: 200,
      offset: 0,
    }));
  }, [dispatch, userId, selectedId]);

  const sorted = _.sortBy(leaderboard || [], "rank");

  // ── Sub-components ──────────────────────────────────────────────────────────
  const Avatar = ({ name, idKey, size }) => (
    <Grid
      container
      sx={{
        width: `${size}px`,
        height: `${size}px`,
        minWidth: `${size}px`,
        borderRadius: "50%",
        backgroundColor: avatarColor(idKey),
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: `${Math.round(size * 0.36)}px`, lineHeight: 1 }}>
        {initials(name)}
      </Typography>
    </Grid>
  );

  const TiebreakerInline = ({ score, fontSize = "12px" }) => {
    const pending = !hasTiebreaker(score);
    return (
      <Typography component="span" sx={{ color: theme.branding.gray[400], fontSize, fontWeight: 400 }}>
        {" "}
        <Typography component="span" sx={{ color: theme.branding.gray[500], fontWeight: 700, fontSize }}>•</Typography>
        {pending ? (
          <Typography component="span" sx={{ color: theme.branding.gray[400], fontStyle: "italic", fontSize }}>{" TB pending"}</Typography>
        ) : (
          <>
            {" TB "}
            <Typography component="span" sx={{ color: theme.branding.gray[600], fontWeight: 700, fontSize }}>{score}</Typography>
          </>
        )}
      </Typography>
    );
  };

  const cardSx = [
    {
      width: "100%",
      borderRadius: "16px",
      border: `1px solid ${theme.branding.gray[200]}`,
      backgroundColor: theme.branding.gray[0],
    },
    (theme) => theme.applyStyles("dark", {
      borderColor: theme.branding.darkBlue[600],
      backgroundColor: theme.branding.darkBlue[800],
    }),
  ];

  return (
    <ContentWrapper>
      <Grid container direction="column" sx={{ width: "100%", gap: "20px" }}>

        {/* ── Header + contest picker ── */}
        <Grid
          container
          direction={isMobile ? "column" : "row"}
          wrap="nowrap"
          sx={{
            gap: "16px",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            padding: "16px 0",
            borderBottom: `1px solid ${theme.branding.gray[200]}`,
          }}
        >
          <Grid container direction="column" sx={{ gap: "4px", width: "auto" }}>
            <Typography variant="h2">Leaderboard Explorer</Typography>
            <Typography variant="subtitle2" sx={{ color: theme.branding.gray[500] }}>
              {meta?.totalEntrants ? `${meta.totalEntrants} entrants` : "Select a contest to view standings"}
            </Typography>
          </Grid>

          <FormControl size="small" sx={{ minWidth: isMobile ? "100%" : "300px" }}>
            <InputLabel>Contest</InputLabel>
            <Select
              value={selectedId}
              label="Contest"
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {CONTESTS.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  <Grid container direction="column">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                    <Typography variant="caption" sx={{ color: theme.branding.gray[400] }}>ID {c.id} · {c.type}</Typography>
                  </Grid>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* ── Empty / loading state ── */}
        {!loading && _.isEmpty(sorted) && (
          <Grid
            container
            sx={[
              ...cardSx,
              {
                padding: isMobile ? "48px 16px" : "96px 16px",
                justifyContent: "center",
                alignItems: "center",
              },
            ]}
          >
            <Typography variant="subtitle1" sx={{ color: theme.branding.gray[400] }}>
              No entrants found for this contest.
            </Typography>
          </Grid>
        )}

        {/* ── Standings table ── */}
        {sorted.length > 0 && (
          <Grid container direction="column" sx={[...cardSx, { padding: "8px", gap: "2px" }]}>

            {/* column header */}
            <Grid
              container
              direction="row"
              wrap="nowrap"
              sx={{ padding: "6px 16px 10px", gap: "12px", borderBottom: `1px solid ${theme.branding.gray[100]}` }}
            >
              <Typography variant="caption" sx={{ width: "34px", minWidth: "34px", color: theme.branding.gray[400], fontWeight: 600 }}>
                Rank
              </Typography>
              <Typography variant="caption" sx={{ flex: 1, color: theme.branding.gray[400], fontWeight: 600 }}>
                Player
              </Typography>
              <Typography variant="caption" sx={{ color: theme.branding.gray[400], fontWeight: 600, textAlign: "right" }}>
                Score
              </Typography>
            </Grid>

            {sorted.map((row) => {
              const handle = displayHandle(row);
              return (
                <Grid
                  key={`explorer-${row.user_id}-${row.rank}`}
                  container
                  direction="row"
                  wrap="nowrap"
                  sx={{
                    padding: "10px 16px",
                    borderRadius: "10px",
                    alignItems: "center",
                    gap: "12px",
                    "&:hover": { backgroundColor: theme.branding.gray[50] },
                  }}
                >
                  {/* rank */}
                  <Typography sx={{ width: "34px", minWidth: "34px", color: theme.branding.gray[500], fontWeight: 600 }}>
                    #{row.rank}
                  </Typography>

                  {/* avatar + name */}
                  <Avatar name={handle} idKey={row.spotlight_user_id} size={36} />
                  <Grid container direction="column" sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                      {handle}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.branding.gray[400] }}>
                      {row.picks_correct} correct
                      <TiebreakerInline score={row.tiebreaker_score} />
                    </Typography>
                  </Grid>

                  {/* points */}
                  <Typography sx={{ color: theme.branding.cyan[600], fontWeight: 700, whiteSpace: "nowrap" }}>
                    {row.points_earned} pts
                  </Typography>
                </Grid>
              );
            })}
          </Grid>
        )}

      </Grid>
    </ContentWrapper>
  );
};

export default LeaderboardExplorer;
